import { slugError } from '../internals/errors/errors';
import { isHttpClient } from '../internals';
import Axios from 'axios';
import { forEach } from './../utils';
export class ShopConfig {
    constructor(config) {
        /**
         *  If it is a config instance.
         */
        this._isConfigInstance = true;
        this._urlUid = 0;
        if (!config) {
            throw slugError('No config provided', ['base', 'config', 'error', 'missing']);
        }
        if (config._isConfigInstance) {
            return config;
        }
        this._setReservator(config.reservator);
        this._setDefaultReservationTime(config.defaultReservationTime);
        this._setBaseUrl(config.baseUrl);
        this._setGuid(config.guid);
        this._setHttpClient(config.httpClient);
        this._setLogLevels(config.logLevels);
        this._setLoggerUrl(config.loggerUrl);
        this.gtmCode = config.gtmCode;
    }
    /**
     *  Set thee reservator settings. Checks if the input is valid and
     *  chooses sensible defaults if no input is given.
     */
    _setReservator(reservator) {
        if (!reservator) {
            reservator = {};
        }
        // noinspection SuspiciousTypeOfGuard
        if (reservator.maxPendingReservations && typeof reservator.maxPendingReservations !== 'number') {
            throw slugError('reservator.maxPendingReservations is not a number', ['shop', 'config', 'error', 'maxPendingReservations']);
        }
        // noinspection SuspiciousTypeOfGuard
        if (reservator.maxConcurrentReservations && typeof reservator.maxConcurrentReservations !== 'number') {
            throw slugError('reservator.maxConcurrentReservations is not a number', ['shop', 'config', 'error', 'maxConcurrentReservations']);
        }
        if (!reservator.maxPendingReservations) {
            reservator.maxPendingReservations = 20;
        }
        if (!reservator.maxConcurrentReservations) {
            // Max concurrent reservations will default to 1, similar to 'in sync'.
            reservator.maxConcurrentReservations = 1;
        }
        this.reservator = reservator;
    }
    /**
     *  Set the default reservation time, Checks if the input is valid and
     *  chooses a sensible default if no input is given.
     */
    _setDefaultReservationTime(defaultReservationTime) {
        // noinspection SuspiciousTypeOfGuard
        if (defaultReservationTime && typeof defaultReservationTime !== 'number') {
            throw slugError('defaultReservationTime is not a number', ['shop', 'config', 'error', 'defaultReservationTime']);
        }
        if (!defaultReservationTime) {
            defaultReservationTime = 8;
        }
        this.defaultReservationTime = defaultReservationTime;
    }
    /**
     *  @internal Function to validate and set the base url.
     */
    _setBaseUrl(url) {
        if (!url) {
            throw slugError('No base url provided', ['base', 'config', 'baseurl', 'error', 'missing']);
        }
        // noinspection SuspiciousTypeOfGuard
        if (typeof url !== 'string') {
            throw slugError(`The provided base url must be a string: ${url}`, ['base', 'config', 'baseurl', 'error', 'type']);
        }
        url = cleanUrl(url);
        const includesProtocol = /^[a-z]+:\/\//i.test(url);
        // Add the https protocol if no protocol is present or throw
        // an error if another protocol than http(s) is present
        if (!includesProtocol) {
            url = `https://${url}`;
        }
        else if (!/^https?:\/\//.test(url)) {
            throw slugError(`The provided url must contain the http(s) protocol: ${url}`, ['base', 'config', 'baseurl', 'error', 'https']);
        }
        this.baseUrl = url;
    }
    /**
     *
     *  Get an absolute path from a relative one
     *
     *  @param path - Relative path
     *  @param filters - The filters to add as GET params
     *
     */
    getUrl(path, filters) {
        let url = this.baseUrl;
        if (path) {
            path = cleanUrl(path);
            if (path.length) {
                url += `/${path}`;
            }
        }
        // Append the filter settings as GET params if provided
        if (filters) {
            const filterKeys = Object.keys(filters);
            // The forwarded URL might already contain a GET param
            url += /\?[\w]/i.test(url) ? '&' : '?';
            forEach(filterKeys, (prop, index) => {
                url += `${prop}=${filters[prop]}`;
                if (index < (filterKeys.length - 1)) {
                    url += '&';
                }
            });
        }
        return encodeURI(url);
    }
    /**
     *  Get an absolute path from a relative one.
     *
     *  A 'nocache' query parameter is added to the data,
     *  to bust any browser micro-cache.
     *
     *  @param path - Relative path
     *  @param filters - The filters to add as GET params
     *
     */
    getNoCacheUrl(path, filters) {
        const queryParams = Object.assign({}, filters || {});
        // Note: Some browsers (micro)-cache XHR requests... so we make the resource id (URI) unique!
        // DD-Frontend-2718
        queryParams.nocache = `${(new Date()).getTime()}-${(++this._urlUid).toString()}-${Math.floor(Math.random() * 1000000000)}`;
        return this.getUrl(path, queryParams);
    }
    /**
     *  @internal Function to validate and set the guid of the shop.
     */
    _setGuid(guid) {
        if (!guid) {
            throw new Error('No guid provided');
        }
        // noinspection SuspiciousTypeOfGuard
        if (typeof guid !== 'string') {
            throw new Error(`The provided guid must be a string: ${guid}`);
        }
        this.guid = guid;
    }
    /**
     *  @internal Internal function to validate and set the http client
     */
    _setHttpClient(httpClient) {
        // Check if the provided httpClient implements the correct interface
        if (httpClient && !isHttpClient(httpClient)) {
            throw new Error('No valid HTTP client provided');
        }
        this.httpClient = httpClient || Axios.create();
    }
    /**
     *  @internal Set the config log levels
     */
    _setLogLevels(logLevels) {
        this.logLevels = logLevels;
    }
    /**
     *  @internal Set the config logger url.
     *
     *  When undefined, no messages will be logged to the external log service
     */
    _setLoggerUrl(loggerUrl) {
        this.loggerUrl = loggerUrl;
    }
}
/**
 *  @internal Used to clean the provided url or path
 *  from leading and trailing slashes.
 */
function cleanUrl(urlOrPath) {
    while (urlOrPath[0] === '/') {
        urlOrPath = urlOrPath.substr(1, urlOrPath.length);
    }
    while (urlOrPath.substr(urlOrPath.length - 1, 1) === '/') {
        urlOrPath = urlOrPath.substr(0, urlOrPath.length - 1);
    }
    return urlOrPath;
}
//# sourceMappingURL=config.js.map